import { VFC, useState, useEffect } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Grid } from "theme-ui";

import { AudienceExplore } from "src/components/audiences/audience-explore";
import { Destinations } from "src/components/clone/destinations";
import { useUser } from "src/contexts/user-context";
import { useAudienceQuery, useCreateAudienceMutation } from "src/graphql";
import { Field } from "src/ui/field";
import { Heading } from "src/ui/heading";
import { Input } from "src/ui/input";
import { PageSpinner } from "src/ui/loading";
import { Wizard } from "src/ui/wizard";
import { Step } from "src/ui/wizard/wizard";
import { useDestinations } from "src/utils/destinations";
import { QueryType, useModelRun, useQueryState } from "src/utils/models";
import { useSource } from "src/utils/sources";

export const CloneAudience: VFC = () => {
  const { audience_id: id } = useParams<{ audience_id: string }>();
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const { user } = useUser();
  const [step, setStep] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [selectedSyncs, setSelectedSyncs] = useState<Set<string>>(new Set());

  const { queryState, initQueryState, setVisualQueryFilter } = useQueryState();

  const { data: audienceData, isLoading: modelLoading } = useAudienceQuery(
    {
      id: id ?? "",
    },
    { enabled: Boolean(id) },
  );

  const { isLoading: creating, mutateAsync: createAudience } = useCreateAudienceMutation();

  const audience = audienceData?.segments_by_pk;
  const parentModel = audience?.parent;
  const syncs = audience?.syncs;

  const {
    data: { definitions },
    loading: destinationsLoading,
  } = useDestinations();

  const { data: source, loading: sourceLoading, error: sourceError } = useSource(audience?.connection?.id);

  const create = async () => {
    await createAudience({
      input: {
        query_type: QueryType.Visual,
        visual_query_parent_id: parentModel?.id,
        visual_query_filter: queryState?.visualQueryFilter,
        name,
        primary_key: parentModel?.primary_key,
        connection_id: source?.id,
        created_by: user?.id != null ? String(user?.id) : undefined,
        destination_instances: {
          data: selectedSyncs.size
            ? syncs
                ?.filter(({ id }) => selectedSyncs.has(String(id)))
                ?.map(({ destination, config, schedule, sync_alerts, row_threshold_attempted, row_threshold_total }) => ({
                  destination_id: destination!.id,
                  config,
                  schedule,
                  schedule_paused: true,
                  created_by: user?.id != null ? String(user?.id) : undefined,
                  row_threshold_attempted,
                  row_threshold_total,
                  alert_instances: {
                    data: sync_alerts.map((alert) => ({
                      alert_id: alert.id,
                      fatal_error: alert.fatal_error,
                      row_error: alert.row_error,
                    })),
                  },
                })) ?? []
            : [],
        },
      },
    });

    addToast(`Cloned ${audience?.name} and ${selectedSyncs?.size} syncs!`, {
      appearance: "success",
    });

    navigate("/audiences");
  };

  const {
    runQuery,
    getSchema,
    cancelQuery,
    resetRunState,
    loading: queryLoading,
    error: queryError,
    rows,
    numRowsWithoutLimit,
    columns,
    transformedSql,
  } = useModelRun(QueryType.Visual, audience?.parent?.columns, {
    modelId: audience?.id,
    variables: { sourceId: source?.id, parentModelId: parentModel?.id, ...queryState },
  });

  useEffect(() => {
    initQueryState(audience);
  }, [audience]);

  useEffect(() => {
    if (syncs) {
      setSelectedSyncs(new Set(syncs.map((sync) => String(sync.id))));
    }
  }, [syncs]);

  if (modelLoading || destinationsLoading || sourceLoading) {
    return <PageSpinner />;
  }

  const steps: Step[] = [
    {
      pageSize: "xlarge",
      title: "Change audience",
      hideContinue: true,
      render: () => (
        <AudienceExplore
          audience={audience}
          cancelQuery={cancelQuery}
          columns={columns}
          error={queryError || sourceError?.message}
          getSchema={getSchema}
          loading={queryLoading || sourceLoading}
          numRowsWithoutLimit={numRowsWithoutLimit}
          parentModel={parentModel}
          reset={resetRunState}
          rows={rows}
          runQuery={runQuery}
          saveLabel="Continue"
          source={source}
          transformedSql={transformedSql}
          onSave={async () => {
            setStep(step + 1);
          }}
          onVisualQueryFilterChange={setVisualQueryFilter}
          {...queryState}
          rowsPerPage={15}
        />
      ),
    },
  ];

  if (syncs?.length) {
    steps.push({
      pageSize: "small",
      title: "Connect destinations",
      render: () => (
        <Destinations definitions={definitions} selected={selectedSyncs} setSelected={setSelectedSyncs} syncs={syncs} />
      ),
    });
  }

  steps.push({
    pageSize: "small",
    title: "Finish",
    disabled: !name,
    loading: creating,
    render: () => {
      return (
        <>
          <Heading sx={{ mb: 8 }} variant="h3">
            Finalize your settings
          </Heading>
          <Grid gap={12}>
            <Field label="Name">
              <Input placeholder={audience?.name} value={name} onChange={setName} />
            </Field>
          </Grid>
        </>
      );
    },
  });

  return (
    <Wizard
      setStep={setStep}
      step={step}
      steps={steps}
      title="Clone an audience"
      onCancel={() => {
        navigate(`/audiences/${id}`);
      }}
      onSubmit={create}
    />
  );
};
